// Auth Context for managing user authentication state
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authAPI, accesoAPI } from '../api/api';

// Cada cuánto, como mínimo, se avisa al servidor de que la sesión sigue en uso
const MS_ENTRE_AVISOS_ACCESO = 30 * 60 * 1000;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage immediately (synchronously)
  const getInitialUser = () => {
    // Check for employee token first
    const empleadoToken = localStorage.getItem('empleado_token');
    const empleadoInfo = localStorage.getItem('empleado_info');
    
    if (empleadoToken && empleadoInfo) {
      try {
        const empleadoData = JSON.parse(empleadoInfo);
        return {
          ...empleadoData,
          empleado_id: empleadoData.id,
          isEmpleadoDirectLogin: true
        };
      } catch (error) {
        console.error('Error parsing empleado_info:', error);
      }
    }
    
    // Check for admin token
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (error) {
        console.error('Error parsing user:', error);
      }
    }
    
    return null;
  };
  
  const getInitialToken = () => {
    return localStorage.getItem('empleado_token') || localStorage.getItem('token') || null;
  };

  const [user, setUser] = useState(getInitialUser);
  const [loading, setLoading] = useState(false); // Start as false since we initialize synchronously
  const [token, setToken] = useState(getInitialToken);

  // Listen for storage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setUser(getInitialUser());
      setToken(getInitialToken());
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Último acceso: el login lo guarda, pero la sesión dura 30 días. Al abrir
  // el ERP con sesión y al volver a la pestaña se avisa al servidor (como
  // mucho cada 30 min aquí; el servidor escribe como mucho una vez por hora).
  const ultimoAvisoAcceso = useRef(0);
  useEffect(() => {
    if (!token) return undefined;
    const avisar = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - ultimoAvisoAcceso.current < MS_ENTRE_AVISOS_ACCESO) return;
      ultimoAvisoAcceso.current = Date.now();
      accesoAPI.sesionActiva().catch(() => { /* no es crítico */ });
    };
    avisar();
    document.addEventListener('visibilitychange', avisar);
    return () => document.removeEventListener('visibilitychange', avisar);
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { access_token, user: userData } = response.data;

      // Una sola sesión en el navegador: al entrar como administrador o
      // dirección se olvida cualquier sesión de empleado anterior (si no, las
      // llamadas seguirían saliendo con la identidad del empleado)
      localStorage.removeItem('empleado_token');
      localStorage.removeItem('empleado_info');
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      setToken(access_token);
      setUser(userData);

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('empleado_token');
    localStorage.removeItem('empleado_info');
    // Fuera también la "Simular vista empleado" (VistaEmpleadoContext)
    try { sessionStorage.removeItem('vista_empleado'); } catch (e) { /* sin sessionStorage */ }
    setToken(null);
    setUser(null);
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed',
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    register,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
